import { Box, Card, Divider, Heading, Text } from '@zillow/constellation-10';
import React from 'react';

import AuthorAvatar from './AuthorAvatar';
import type { AuthorCardProps } from './AuthorPage.types';
import Expertise from './Expertise';

/**
 * Card displaying the author's avatar, name, job title, and expertise.
 */
const AuthorCard: React.FC<AuthorCardProps> = ({ author }) => {
    return (
        // v8 <Box as={Card} cardType="secondary"> → plain v10 Card. cardType is
        // gone in v10; "secondary" only meant "no primary accent border", which
        // is the v10 default (tone="neutral", outlined, elevated).
        //
        // v10 Card merges the css prop through Panda's css(), so the v8 '&&&&'
        // specificity hack for beating Card's own padding is no longer needed.
        // v8 spacing xl (48px) → layout.looser, md (24px) → loose.
        <Card
            css={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                paddingBlock: 'layout.looser',
                paddingInline: 'loose',
            }}
        >
            <AuthorAvatar
                avatar={author.avatar}
                name={author.name}
                size="xxl"
            />
            <Box css={{ display: 'none', lg: { display: 'block' } }}>
                {/* v8 fontColor="blue600" is SkunkWorks #001962 = v10 blue.900
                    exactly (see COLOR_MAP textDarkBlue). Kept on the primitive so
                    it does not invert in dark mode.
                    v8 was <Text as="h3" fontType="h5">, i.e. an h3 in the outline
                    rendered at h5's size, so textStyle heading-xs (20px = v8 h5)
                    is load-bearing here — level={3} on its own would default to
                    heading-md (36px). Declared via css to match the other
                    textStyles on this page; see the note in Expertise.tsx for why
                    css is the form used throughout. */}
                <Heading
                    level={3}
                    css={{
                        textStyle: 'heading-xs',
                        color: 'blue.900',
                        marginBottom: 'tighter',
                        textAlign: 'center',
                    }}
                >
                    {author.name}
                </Heading>
                <Text
                    asChild
                    textStyle="body"
                    css={{
                        color: 'blue.900',
                        marginBottom: 'looser',
                        textAlign: 'center',
                    }}
                >
                    <p>{author.jobTitle}</p>
                </Text>
            </Box>
            {author.expertise && author.expertise.length > 0 && (
                <>
                    {/* v8 length="fixed" was removed in v10 (length?: never), so the
                        144px width is restored explicitly. v10 tone="muted" resolves
                        to gray.300 = #CDCDD3, matching the v8 divider color exactly.
                        v8 marginBottom lg (32px) → looser. */}
                    <Divider
                        tone="muted"
                        css={{ width: '3600', marginBottom: 'looser' }}
                    />
                    <Expertise expertise={author.expertise} />
                </>
            )}
        </Card>
    );
};

export default AuthorCard;
